import React, {
    FunctionComponent,
    ReactElement,
    useMemo,
    memo,
    useEffect,
    MutableRefObject,
} from 'react'
import Grid from '@sport1/react-elements/Grid'
import Image from '@sport1/react-elements/Image'
import times from 'lodash/times'
import { View } from 'react-native'
import { LayoutItemSize } from '../../LayoutTypes'
import { getHeightProps, spaceMap } from '../../LayoutUtils'
import { BackgroundColorTypes } from '../../../Theme/Fonts/FontTypes'
import { TestID } from '../../../utils/PropTypesDefinitions'
import { logComponentDeprecation } from '../../../utils/Deprecation'

type OneColumnLayoutProps = {
    innerRef?: MutableRefObject<(ReactElement & View) | null>
    spaceBottom?: 'small' | 'standard'
    spaceX?: 'small' | 'standard'
    numberOfRows: number
    renderRow: (index: number) => ReactElement | null
    rowHeights?: LayoutItemSize[]
    expand?: boolean
    backgroundColor?: BackgroundColorTypes
    backgroundImage?: string
    children?: never
    center?: boolean
    collapsable?: boolean
    pointerEvents?: 'none' | 'auto' | 'box-none' | 'box-only'
} & TestID

const OneColumnLayout: FunctionComponent<OneColumnLayoutProps> = ({
    innerRef,
    spaceX,
    spaceBottom,
    numberOfRows,
    renderRow,
    rowHeights = [],
    expand = false,
    backgroundColor = 'tahitian-4',
    backgroundImage,
    center,
    collapsable,
    pointerEvents,
    testID,
}): ReactElement => {
    /* istanbul ignore next */
    useEffect(() => {
        logComponentDeprecation('OneColumnLayout')
    }, [])

    const rows = useMemo(() => {
        const tmpRows: ReactElement[] = []
        times(numberOfRows, (i): void => {
            tmpRows.push(
                <Grid
                    key={`row_${i}`}
                    {...getHeightProps(rowHeights[i])}
                    centerHorizontal={center}
                    pointerEvents={pointerEvents}
                >
                    {renderRow(i)}
                </Grid>
            )
        })

        return tmpRows
    }, [center, numberOfRows, pointerEvents, renderRow, rowHeights])

    return (
        <Grid
            testID={testID}
            ref={innerRef}
            collapsable={collapsable}
            position="relative"
            noExpand={!expand}
            pointerEvents={pointerEvents}
        >
            <Grid
                noExpand={!expand}
                paddingX={spaceMap.get(spaceX)}
                paddingBottom={spaceMap.get(spaceBottom)}
                zIndex={1}
                backgroundColor={!backgroundImage ? backgroundColor : undefined}
                centerVertical={center}
                pointerEvents={pointerEvents}
            >
                {rows}
            </Grid>
            {backgroundImage ? (
                <Grid
                    noExpand
                    position="absolute"
                    width="100%"
                    height="100%"
                    zIndex={0}
                    pointerEvents={pointerEvents}
                >
                    <Image src={backgroundImage} width="100%" height="100%" />
                </Grid>
            ) : null}
        </Grid>
    )
}

export default memo(OneColumnLayout)
